import React, {useState}  from 'react';
import classnames from 'classnames';
import CardBuilderConnected from './CardBuilderConnected.jsx';
import {FormattedHTMLMessage, FormattedMessage, useIntl} from 'react-intl';
import QuestionGroupHeader from '../QuestionGroupHeader.jsx';
import {getMessages, getMessageValue} from '../../../i18n/messageUtil.mjs';
import {parseAnswerValue} from '../../../util/questionUtil.jsx';
import builderGetLinkedQuestions from '../../../util/builderGetLinkedQuestions.mjs';
import {QuestionGroupViewPropTypes} from '../../../shapes/index.mjs';
import builderMessages from '@ignitesales/react-card-question-builder/src/messages';
import {cssBEM as cardBuilderCssBem} from '@ignitesales/react-card-question-builder';
import {useText} from '../../TextContext';

const CardBuilder = ({questionGroup, className, renderQuestion, page, customProps}) => {
  const intl = useIntl();
  const builderKey = questionGroup.attributes.questionGroupKey;
  const builderOptions = questionGroup.questions.find(q => q.appkey === builderKey);
  const classes = classnames(className, 'questionGroup--cardBuilder');
  const searchInputLabel = getMessageValue(intl.locale, `${builderKey}.cardBuilder.input.label`);
  const searchInputPlaceholder = getMessageValue(intl.locale, `${builderKey}.cardBuilder.input.placeholder`);
  const textContext = useText();

  const [filteredOptions, setFilteredOptions] = useState(builderOptions);

  const searchCallbackFn = (value) => {
    let answers = getAnswersWithTags().filter(option => 
      option.key.toLowerCase().includes(value.toLowerCase()) || option.answerTags.toLowerCase().includes(value.toLowerCase())
    );

    setFilteredOptions({
      ...builderOptions,
      answers
    });
  };

  const getAnswersWithTags = () => {
    return (builderOptions.answers || []).map(row => {
      return {
        key: row.key,
        attributes: row.attributes,
        answerTags: getMessageValue(intl.locale, `${builderOptions.appkey}.${row.key}.tags`)
      };
    });
  };

  const prepareBuilderLinkedQuestions = () => {
    return (builderOptions.answers || []).map(row => {
      const messageKey = `${builderOptions.appkey}.${row.key}`;
      const messageKey1 = `${messageKey}.cardLabel`;
      return {
        id: row.key,
        labelMessageId:
          textContext?.[`${messageKey1}`] ||
          textContext?.[`${messageKey}`] ||
          (getMessageValue(intl.locale, messageKey1) ? messageKey1 : messageKey),
        questions: builderGetLinkedQuestions(questionGroup, row.attributes.linkedQuestions || []),
      };
    });
  };

  const getBuilderMessages = () => {
    const customMessages = {
      'cardBuilder.addCard':
        getMessageValue(intl.locale, `${builderKey}.cardBuilder.addCard`) || builderMessages['cardBuilder.addCard'],
      'cardBuilder.cancelEdit':
        getMessageValue(intl.locale, `${builderKey}.cardBuilder.cancelEdit`) ||
        builderMessages['cardBuilder.cancelEdit'],
      'cardBuilder.editCard':
        getMessageValue(intl.locale, `${builderKey}.cardBuilder.editCard`) || builderMessages['cardBuilder.editCard'],
      'cardBuilder.removeCard':
        getMessageValue(intl.locale, `${builderKey}.cardBuilder.removeCard`) ||
        builderMessages['cardBuilder.removeCard'],
      'cardBuilder.saveCard':
        getMessageValue(intl.locale, `${builderKey}.cardBuilder.saveCard`) || builderMessages['cardBuilder.saveCard'],
      'cardBuilder.modal.title': getMessageValue(intl.locale, `${builderKey}.cardBuilder.modal.title`),
    };
    return {
      ...getMessages(intl.locale),
      ...customMessages,
    };
  };

  const renderCardBodyFn = ({card, isActive}) => {
    let render = null;
    if (isActive) {
      render = card.questions.map(q => {
        const answerValue =
          textContext?.[`${page?.answers?.[`${q.appkey}`]}`] ||
          (Object.prototype.hasOwnProperty.call(page.answers, q.appkey)
            ? parseAnswerValue(intl, page.answers[q.appkey])
            : null);
        const messageKey = `${q.appkey}.cardBuilder.answerLabel`;
        return answerValue ? (
          <div className={`${cardBuilderCssBem}__answer`} key={q.appkey}>
            <span className={`${cardBuilderCssBem}__answer-label`}>
              {textContext?.[`${messageKey}`] || (
                <FormattedMessage id={getMessageValue(intl.locale, messageKey) ? messageKey : q.appkey} />
              )}
            </span>
            <span className={`${cardBuilderCssBem}__answer-value`}>{answerValue}</span>
          </div>
        ) : null;
      });
    }
    return render;
  };

  const renderModalBodyFn = card =>
    textContext?.[`${card.id}`]?.cardBuilder?.body ||
    (getMessageValue(intl.locale, `${card.id}.cardBuilder.body`) && (
      <FormattedHTMLMessage id={`${card.id}.cardBuilder.body`} />
    ));

  const builderProps = {
    builderLinkedQuestions: prepareBuilderLinkedQuestions(),
    builderOptions,
    locale: intl.locale,
    messages: getBuilderMessages(),
    nodeId: questionGroup.attributes.modalNodeId || 'root',
    page,
    showAllMode: questionGroup.attributes.showAllMode || false,
    questionGroup,
    renderLinkedQuestion: renderQuestion,
    renderOptions: renderQuestion,
    renderCardBodyFn,
    renderModalBodyFn,
    searchCallbackFn,
    searchInputLabel,
    searchInputPlaceholder,
    showSearchFilter: questionGroup.attributes.showSearchFilter || false,
    filteredOptions,
    ...customProps,
  };

  return (
    <section className={classes}>
      <QuestionGroupHeader questionGroup={questionGroup} />
      <section>
        <CardBuilderConnected {...builderProps} />
      </section>
    </section>
  );
};

CardBuilder.propTypes = QuestionGroupViewPropTypes;

export default CardBuilder;
